import React, { useMemo, useRef } from 'react';
import { Megaphone, Sparkles, Compass, ArchiveX } from 'lucide-react';
import { Badge, Card, CardContent, Alert, AlertDescription } from '@ncoder/ui';
import { PageHeader } from '@/components/molecules/PageHeader';
import { OnThisPageNav, type TocSection } from '@/components/molecules/docs/OnThisPageNav';
import { cn } from '@/lib/utils';
import { releaseNotes, type ReleaseFeatureItem, type ReleaseNote } from './data';

function slugify(title: string) {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-+|-+$)/g, '');
}

function buildToc(release: ReleaseNote): TocSection[] {
  return [
    {
      id: 'new-features',
      label: 'New features',
      groups: release.newFeatures.map((g) => ({
        label: g.label,
        items: g.items.map((i) => ({ id: slugify(i.title), title: i.title })),
      })),
    },
    {
      id: 'existing-features',
      label: 'Existing features',
      groups: [{ items: release.existingFeatures.map((i) => ({ id: slugify(i.title), title: i.title })) }],
    },
    {
      id: 'deprecated-features',
      label: 'Deprecated features',
      groups: release.deprecatedFeatures.map((g) => ({
        label: g.label,
        items: g.items.map((i) => ({ id: slugify(i.title), title: i.title })),
      })),
    },
  ];
}

function FeatureRow({
  item,
  index,
  tone,
  id,
}: {
  item: ReleaseFeatureItem;
  index: number;
  tone: 'new' | 'existing' | 'deprecated';
  id: string;
}) {
  const numberClasses = {
    new: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
    existing: 'bg-primary/10 text-primary',
    deprecated: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  }[tone];

  return (
    <Card id={id} className="scroll-mt-4">
      <CardContent className="flex items-start gap-4 p-5">
        <div
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
            numberClasses
          )}
        >
          {index + 1}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <h3 className="text-base font-medium text-foreground">
              {item.title}
              {item.beta && (
                <Badge variant="outline" className="ml-2 align-middle text-[10px] uppercase tracking-wide">
                  Beta
                </Badge>
              )}
            </h3>
            {item.replacedBy && (
              <Badge
                variant="outline"
                className="border-transparent bg-amber-500/15 text-[11px] font-medium text-amber-600 dark:text-amber-400"
              >
                Deprecated · replaced by {item.replacedBy}
              </Badge>
            )}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
          {item.image && (
            <div
              className="mt-4 overflow-hidden rounded-lg border border-border bg-muted/30"
              style={item.imageAspect ? { aspectRatio: item.imageAspect } : undefined}
            >
              <img
                src={item.image}
                alt={item.title}
                loading="lazy"
                className="block h-full w-full object-cover"
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export const ReleaseNotes: React.FC = () => {
  const release = releaseNotes[0];
  const toc = useMemo(() => buildToc(release), [release]);
  const scrollRef = useRef<HTMLDivElement>(null);

  return (
    <div className="flex h-full flex-col gap-4 animate-fade-in bg-background">
      <PageHeader
        icon={Megaphone}
        title="Release Notes"
        description="See what's new, changed, and deprecated in nCoder.ai."
      />

      <div className="flex min-h-0 flex-1 gap-6 overflow-hidden">
        <div ref={scrollRef} className="custom-scrollbar min-w-0 flex-1 overflow-y-auto pr-1">
          <div className="mx-auto max-w-2xl space-y-3 pb-8 text-center">
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{release.eyebrow}</div>
            <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
              {release.title} <span className="text-primary">{release.titleEmphasis}</span>
            </h1>
            <p className="text-muted-foreground">{release.lede}</p>
            <div className="font-mono text-xs text-muted-foreground/80">
              {release.version} &nbsp;·&nbsp; {release.date}
            </div>
          </div>

          <section id="new-features" className="scroll-mt-4 space-y-4 pt-8">
            <div className="flex items-center gap-2">
              <Badge className="gap-1 border-transparent bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                <Sparkles className="h-3 w-3" />
                New
              </Badge>
              <h2 className="text-xl font-semibold">New features</h2>
            </div>
            <p className="text-sm text-muted-foreground">What's landing in this release.</p>
            <div className="space-y-6">
              {release.newFeatures.map((group) => (
                <div key={group.label ?? 'ungrouped'} className="space-y-3">
                  {group.label && (
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {group.label}
                    </h4>
                  )}
                  <div className="space-y-3">
                    {group.items.map((item, index) => (
                      <FeatureRow key={item.title} item={item} index={index} tone="new" id={slugify(item.title)} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section id="existing-features" className="scroll-mt-4 space-y-4 pt-10">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="gap-1">
                <Compass className="h-3 w-3" />
                Tour
              </Badge>
              <h2 className="text-xl font-semibold">Existing features</h2>
            </div>
            <p className="text-sm text-muted-foreground">
              A walkthrough of everything already in nCoder.ai, in the order you'll meet it.
            </p>
            <div className="space-y-3">
              {release.existingFeatures.map((item, index) => (
                <FeatureRow key={item.title} item={item} index={index} tone="existing" id={slugify(item.title)} />
              ))}
            </div>
          </section>

          <section id="deprecated-features" className="scroll-mt-4 space-y-4 pb-8 pt-10">
            <div className="flex items-center gap-2">
              <Badge className="gap-1 border-transparent bg-amber-500/15 text-amber-600 dark:text-amber-400">
                <ArchiveX className="h-3 w-3" />
                Sunset
              </Badge>
              <h2 className="text-xl font-semibold">Deprecated features</h2>
            </div>
            <Alert className="border-amber-500/30 bg-amber-500/10">
              <AlertDescription className="text-amber-700 dark:text-amber-400">
                These features are deprecated as of this release and will be removed in a future version. Migration
                guidance is provided for each.
              </AlertDescription>
            </Alert>
            <div className="space-y-6">
              {release.deprecatedFeatures.map((group) => (
                <div key={group.label ?? 'ungrouped'} className="space-y-3">
                  {group.label && (
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {group.label}
                    </h4>
                  )}
                  <div className="space-y-3">
                    {group.items.map((item, index) => (
                      <FeatureRow
                        key={item.title}
                        item={item}
                        index={index}
                        tone="deprecated"
                        id={slugify(item.title)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        <OnThisPageNav sections={toc} scrollContainerRef={scrollRef} />
      </div>
    </div>
  );
};

export default ReleaseNotes;
